import {GroupField, ImageField, isFilled, KeyTextField, LinkField} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next'
import {useContext, useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {Icon} from 'src/common/Icon'
import {TextLink} from 'src/common/TextLink'
import {NotificationContext} from 'src/context/NotificationContext'
import cn from 'classnames'

type NotificationBannerProps = {
  message: KeyTextField
  linkField: LinkField
  linkText: KeyTextField
  dataType: KeyTextField
  notificationId: KeyTextField
  pageName?: string
  images: GroupField<{image: ImageField}>
}

export const NotificationBanner = ({
  message,
  linkField,
  linkText,
  dataType,
  notificationId,
  pageName,
  images,
}: NotificationBannerProps) => {
  const placeholderRef = useRef<HTMLElement | null>(null)
  const {isActive, disableNotification} = useContext(NotificationContext) || {}
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isMounted, setIsMounted] = useState(false)
  const containsImages = isFilled.group(images)

  // Get placeholder element
  useEffect(() => {
    placeholderRef.current = document.getElementById('header-placeholder')
    setIsMounted(true)
  }, [])

  return (
    isActive &&
    isMounted &&
    disableNotification && (
      <>
        <div
          className={cn('text-body-s w-full bg-moss-green-500 text-moss-white', {
            'py-3': containsImages,
            'py-4': !containsImages,
          })}
          ref={containerRef}
          id="notification-banner"
        >
          <div className="mx-auto flex max-w-[1200px] items-center justify-center gap-3 px-5 xl:px-0">
            <div className="flex w-full flex-col items-center justify-center gap-3 md:flex-row md:gap-3">
              <Assets images={images} />
              <span className="text-center ">{message}</span>
              {linkText && (
                <TextLink
                  variation="small"
                  label={linkText}
                  field={linkField}
                  mktgId={notificationId!}
                  dataType={dataType!}
                  location="component-0"
                  pageName={pageName}
                  color="accent-neon"
                />
              )}
            </div>
            <button className="h-5 self-start md:self-center" onClick={disableNotification}>
              <Icon name="cross" className="h-5 w-5" />
            </button>
          </div>
        </div>
        {placeholderRef.current &&
          isMounted &&
          createPortal(
            <div className="w-full" style={{height: (containerRef.current?.clientHeight || 0) + 64}} />,
            placeholderRef.current!,
          )}
      </>
    )
  )
}

const Assets = ({images}: {images: GroupField<{image: ImageField}>}) => {
  if (isFilled.group(images)) {
    return <PrismicNextImage field={images[0].image} className="h-7 w-min" />
  }

  return null
}
